import 'crypto-js/core';
import hmacSHA256 from 'crypto-js/hmac-sha256';
import Base64 from 'crypto-js/enc-base64';
import Config from 'react-native-config';
import {getDeviceId, getModel, getVersion} from 'react-native-device-info';
import RNFetchBlob from 'rn-fetch-blob';
import {Platform} from 'react-native';
import {unzip} from 'react-native-zip-archive';
import label from './label';

const plansDir = `${RNFetchBlob.fs.dirs.DocumentDir}/plans`;

export const isWeb = Platform.OS === 'web'

const serverUrl =
  isWeb ? process.env.EXPO_SERVER_URL : Config.SERVER_URL;

const apiKey =
  isWeb ? process.env.EXPO_API_KEY : Config.API_KEY;

const apiSecret =
  isWeb ? process.env.EXPO_API_SECRET : Config.API_SECRET;

export const lambdaUrl =
  isWeb ? process.env.EXPO_LAMBDA_URL : Config.LAMBDA_URL;

export const contentUrl =
  isWeb ? process.env.EXPO_CLOUDFRONT_URL : `file://${plansDir}`;

type PlanStatus = 'Publish' | 'Draft';

type Plan = {
  PlanID: string;
  Enabled: boolean;
  IsActive: boolean;
  Name: string;
  IconMediaID: string;
  Version: string; // map to number
  PublishedDate: string; // map to date
  PlanContentPackageID: string;
  PlanContentPackageSize: number;
  UpdateRequired: boolean;
  ContentOwner: string;
  PreviewSearchTerm: string;
  ExpiredDate: string; // map to date
  PlanStatus: PlanStatus;
  IsExpired: boolean;
  RestrictWebAccess: boolean;
};

export type PlanSearchResult = Plan & {
  OrganizationName: string;
  OrganizationSearchTerm: string;
  Description: string;
  SearchGroup: string;
  SearchTerm?: string;
  SearchTermPasswordRequired: boolean;
  PreviewSearchTermPasswordRequired: boolean;
};

export type PlanInfo = Plan & {
  NavBarBackgroundColor: string; // map to hex value
  NavBarTextColor: string; // map to hex value
  PlanDescription: string;
  Contacts: Contact[];
  SearchableTerm: string;
  Organization: string;
  Contents: Contents[];
};

export type Contact = {
  ContactID: string;
  Title: string;
  Name: string;
  Address1: string;
  Address2?: string;
  City: string;
  State: string;
  PostalCode: string;
  Phone1: string;
  Phone2?: string;
  Phone3?: string;
  Phone4?: string;
  Phone1Label: string;
  Phone2Label?: string;
  Phone3Label?: string;
  Phone4Label?: string;
  OtherInfo: string;
  CompanyName: string;
  Email: string;
};

export type MediaInfo = {
  MediaID: string;
  LastUpdateUtc: string;
  MediaUrl: string;
  MimeType: string;
  DownloadToken: string;
  Size: number;
  MetaData: null; // ??
  MediaName: string;
};

export type Contents =
  | ListContents
  | ExpandableContents
  | HtmlContents
  | FileContents;

export type ContentMeta = {
  Title: string;
  LastUpdate: string; // maps to date
};

export type ContentDisplay = {
  DisplayOrder: number;
  BackgroundColor: string; // map to hex value
  TextColor: string; // map to hex value
  Icon: null; // ??
};

type BaseContent = ContentMeta & {
  ContentID: string;
  IsRootContent: boolean;
  Type: 'L' | 'E' | 'H' | 'F' | 'A'; // List | Expandable | Html | File | Audio
};

export type ListContents = BaseContent & {
  ListContents: ListContent[];
};

export type ListContent = ContentDisplay &
  ContentMeta & {
    ParentPlanContentID: string;
    NextPlanContentID: string;
    ListContentID: string;
  };

export type ExpandableContents = BaseContent & {
  ExpandableContents: ExpandableContent[];
};

export type ExpandableContent = ContentDisplay &
  ContentMeta & {
    ExpandableContentID: string;
    ExpandableContentHTMLFile: string;
  };

export type HtmlContents = BaseContent & {
  IndexFile: string;
};

export type FileContents = BaseContent & {
  File: string;
};

export type PlanFormEmailData = {
  body: string;
  emailTo: string[];
  subject: string;
};

type RequestMethod = 'GET' | 'POST';

const deviceInfo = () => {
  return {
    UserDeviceID: getDeviceId(),
    UserDeviceType: Platform.OS === 'ios' ? 'I' : 'A',
    UserDeviceOSVer: getVersion(),
    UserDeviceModelName: getModel(),
    UserDeviceCarrier: '',
    UserDeviceManufacture: Platform.OS === 'ios' ? 'Apple' : 'Google',
  };
};

class ServerError extends Error {}

const signedHeaders = (method: RequestMethod, url: string, body?: string) => {
  const timestamp = Date.now() / 1000;
  const salt = `${apiKey}${apiSecret}${timestamp}`;
  const message = `${method}${url}${salt}${body || ''}`;
  const signature: string = Base64.stringify(hmacSHA256(message, salt));

  return {
    'Content-Type': 'application/json',
    'Auth-Token': apiKey!,
    Timestamp: timestamp.toString(),
    Signature: signature,
  };
};

const makeRequest = (
  targetApi: 'legacy'|'lambda',
  method: RequestMethod,
  url: string,
  body?: string,
): Promise<any> => {
  const headers = signedHeaders(method, url, body);
  const fetchUrl = {
    legacy: url,
    lambda: lambdaUrl + url
  };
  return fetch(
    fetchUrl[targetApi],
    {
      method,
      headers,
      body: body || null,
      // A cached PlanInfo response can make an old password appear valid and
      // leave the browser on stale plan content. Native requests are unchanged.
      cache: isWeb && method === 'GET' ? 'no-store' : undefined,
    },
  )
    .then((resp) => resp.json())
    .then((resp) => {
      // responses look different from new api with email method
      if (
        Object.prototype.hasOwnProperty.call(resp, 'error') ||
        (Object.prototype.hasOwnProperty.call(resp, 'status') &&
          resp.status !== 'success')
      ) {
        throw new ServerError(resp.message || resp.error?.message);
      }

      return resp.data || resp.message;
    })
};

const zipRequest = async (
  method: RequestMethod,
  url: string,
  body?: string,
): Promise<any> => {
  const headers = signedHeaders(method, url, body);

  try {
    // Download temp zip file
    const resp = await RNFetchBlob.config({
      fileCache: true,
    }).fetch(method, url, headers, body);
    // Check for errors
    if (resp.respInfo.status !== 200) {
      throw new ServerError(resp.data.message);
    }
    // Unzip file to documents directory
    const path = await unzip(resp.data, plansDir, 'UTF-8');
    // Remove temp zip file
    resp.flush();
    return path;
  } catch (e) {
    console.error(e);
  }
};

const api = {
  getInitialPlans: () => {
    return makeRequest('legacy',
      'GET',
      `${serverUrl}/searchForInitialPlan?appName=${label.appName}`,
    ).then((resp) => resp.Plans);
  },
  getPlansByGroup: (group: string) => {
    return makeRequest('legacy',
      'GET',
      `${serverUrl}/SearchByGroup?appName=${label.appName}&groupName=${group}`,
    ).then((resp) => resp.Plans);
  },
  searchPlans: (term: string) => {
    return makeRequest('legacy',
      'GET',
      `${serverUrl}/search?appName=${
        label.appName
      }&keywords=${fixedEncodeURIComponent(term)}`,
    ).then((resp) => resp.Plans);
  },
  getVersions: (planIds: string[]) => {
    return makeRequest('legacy',
      'POST',
      `${serverUrl}/Versions`,
      JSON.stringify({planIds: planIds.map((id: string) => ({PlanID: id}))}),
    ).then((resp) => resp.Plans);
  },
  getPlanInfo: (id: string, password: string = '') => {
    return makeRequest('legacy',
      'GET',
      `${serverUrl}/PlanInfo?pid=${id}&password=${fixedEncodeURIComponent(password)}`,
    ).then((resp) => resp.Plan);
  },
  getPlanInfoByLicense: (license: string) => {
    return makeRequest('legacy',
      'GET',
      `${serverUrl}/PlanInfoByLicense?license=${license}`,
    ).then((resp) => resp.Plan);
  },
  checkLicenseIsValid: (license: string) => {
    return makeRequest('legacy',
      'GET',
      `${serverUrl}/IsLicenseActive?license=${license}`,
    );
  },
  getMediaInfo: (id: string) => {
    return makeRequest('legacy','GET', `${serverUrl}/mediaInfo?mediaId=${id}`);
  },
  downloadMedia: (token: string) => {
    return zipRequest(
      'POST',
      `${serverUrl}/DownloadMedia`,
      JSON.stringify({
        DownloadToken: token,
        AppVersion: '1',
        AppID: '',
        ...deviceInfo(),
      }),
    );
  },
  downloadMediaByLicense: (
    firstName: string,
    lastName: string,
    email: string,
    key: string,
  ) => {
    return zipRequest(
      'POST',
      `${serverUrl}/DownloadMediaByLicense`,
      JSON.stringify({
        FirstName: firstName,
        LastName: lastName,
        EmailAddress: email,
        LicenseKey: key,
        AppVersion: '1',
        AppID: '',
        ...deviceInfo(),
      }),
    );
  },
  sendPlanFormEmail: (emailData: PlanFormEmailData) => {
    return makeRequest('lambda', 'POST', `/sendPlanFormEmail`, JSON.stringify(emailData));
  },
};


//https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURIComponent
const fixedEncodeURIComponent = (str: string) => encodeURIComponent(str).replace(/[!'()*]/g, (char) => `%${char.charCodeAt(0).toString(16)}`);

export default api;
